/**
 * CharacterList — a mesa em cartões.
 *
 * Redesenhada em 2026-07-31 (fase 4 de docs/PLANO_MEMBROS_E_FICHA.md, mockups
 * aprovados pelo Paulo). A lista e a ficha já usavam as mesmas CORES; o que
 * faltava era a mesma FORMA. Cinco elementos vieram emprestados da ficha —
 * cartão, anel violeta com selo de nível, barra de HP de três cores, chip de
 * ícone tingido e pílula de papel — na densidade de lista.
 *
 * Por que importa: com "Membros" virando aba, lista e ficha ocupam a MESMA
 * área, uma substituindo a outra. Compartilhando o vocabulário, a transição se
 * lê como zoom no mesmo objeto em vez de salto para outra tela.
 *
 * O maior ganho é a barra de HP. Como texto, ninguém varre uma coluna de
 * números procurando quem está ferido; como barra, a mesa se lê num relance.
 */

import { useMemo } from 'react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Heart, Zap, Plus, Sword, MessageCircle } from 'lucide-react'
import { getCharacterType, getCharacterTypeStyle } from '@/utils/characterUtils'
import { cn } from '@/lib/utils'
import type { Character } from '@/types'
import type { Member as MemberType } from '@/types/member'
import type { UnreadCounts } from '@/hooks/useUnreadCounts'

interface CharacterListProps {
  characters: Character[];
  onSelect?: (character: Character | null) => void;
  membersByUserId?: Map<string, MemberType>;
  disableSelection?: boolean;
  onChatClick?: (character: Character) => void;
  currentUserId?: string;
  unreadCounts?: UnreadCounts; // Contagem de mensagens não lidas por usuário
  isUserOnline?: (userId: string) => boolean; // Função para verificar status online
}

/** As mesmas três faixas da ficha: quem lê a lista e quem lê a ficha vê a
 *  mesma cor para o mesmo estado. */
function faixaDeHp(atual: number, total: number) {
  const pct = total > 0 ? Math.max(0, Math.min(100, (atual / total) * 100)) : 0
  if (pct > 60) return { pct, texto: 'text-emerald-500 dark:text-emerald-400', barra: 'from-emerald-600 to-emerald-400' }
  if (pct > 30) return { pct, texto: 'text-amber-500 dark:text-amber-400', barra: 'from-amber-600 to-amber-400' }
  return { pct, texto: 'text-rose-500 dark:text-rose-400', barra: 'from-rose-600 to-rose-400' }
}

export function CharacterList({ characters, onSelect, membersByUserId, disableSelection = false, onChatClick, currentUserId, unreadCounts = {}, isUserOnline }: CharacterListProps) {
  // Mestre da mesa (para a presença dos NPCs sem controlador explícito)
  const masterUserId = useMemo(() => {
    if (!membersByUserId) return undefined
    for (const m of membersByUserId.values()) {
      if (m.role === 'mestre') return m.userId
    }
    return undefined
  }, [membersByUserId])

  // Presença por linha: NPC não conecta (userId sintético) — sua presença é a
  // de quem o controla (mestre). Jogador: a própria conexão.
  const isRowOnline = (character: Character): boolean => {
    if (!isUserOnline) return false
    const presenceId = character.isNpc
      ? (character.controlledByUserId || masterUserId || character.userId)
      : character.userId
    return isUserOnline(presenceId)
  }

  // Ordenar personagens: online primeiro, depois offline, ambos alfabeticamente
  const sortedCharacters = useMemo(() => {
    if (!isUserOnline) return characters

    return [...characters].sort((a, b) => {
      const aOnline = isRowOnline(a) ? 1 : 0
      const bOnline = isRowOnline(b) ? 1 : 0

      // Primeiro por status online (online primeiro)
      if (aOnline !== bOnline) {
        return bOnline - aOnline
      }

      // Depois por nome alfabeticamente
      return a.name.localeCompare(b.name, 'pt-BR')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [characters, isUserOnline, masterUserId])

  const cartao = (character: Character) => {
    // Presença da linha (NPC herda a do controlador/mestre)
    const isOnline = isRowOnline(character)
    const characterType = getCharacterType(character, membersByUserId)
    const typeStyle = getCharacterTypeStyle(characterType)

    // Dados reais do personagem (exibe inclusive 0 — ex.: HP de quem caiu)
    const level = character.level
    const hpCurrent = character.hpCurrent
    const hpTotal = character.hpTotal
    const points = character.points
    const hp = faixaDeHp(hpCurrent, hpTotal)

    const isOwnCharacter = character.userId === currentUserId
    const unreadCount = unreadCounts[character.userId] || 0

    const tituloDaPresenca = character.isNpc
      ? (isOnline ? 'Online (mestre conectado)' : 'Offline (mestre ausente)')
      : (isOnline ? 'Online' : 'Offline')

    return (
      // O cartão é um DIV com dois botões IRMÃOS — abrir a ficha e o chat
      // privado. Botão dentro de botão é HTML inválido e armadilha de teclado.
      <div
        key={character.id}
        className={cn(
          'flex items-center gap-2.5 rounded-xl border p-2.5 transition-colors',
          'border-game-border-primary bg-game-tertiary dark:border-zinc-700/50 dark:bg-zinc-800/50',
          !disableSelection && 'hover:border-violet-500/40 hover:bg-game-hover dark:hover:bg-white/[0.06]'
        )}
      >
        <button
          type="button"
          disabled={disableSelection}
          onClick={() => onSelect?.(character)}
          aria-label={`Abrir ficha de ${character.name}`}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-lg text-left disabled:cursor-default"
        >
          {/* Avatar com anel violeta e selo de nível: a assinatura da ficha,
              aqui em miniatura (anel de 2px onde a ficha usa 4px). */}
          <div className="relative flex-shrink-0">
            <Avatar className="h-11 w-11 ring-2 ring-violet-500/50">
              <AvatarImage src={character.imageUrl} alt="" />
              <AvatarFallback className="bg-violet-600 text-sm font-bold text-white">
                {character.name[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>

            <span className="absolute -bottom-1 -right-1 rounded-full bg-amber-500 px-1.5 text-[10px] font-bold leading-4 text-zinc-900 shadow">
              Nv.{level}
            </span>

            {isUserOnline && (
              <span
                data-testid={`presenca-${character.id}`}
                data-online={isOnline ? 'sim' : 'nao'}
                title={tituloDaPresenca}
                className={cn(
                  'absolute -bottom-0.5 -left-0.5 h-3 w-3 rounded-full border-2 border-game-tertiary dark:border-zinc-800',
                  isOnline ? 'bg-emerald-500' : 'bg-zinc-500'
                )}
              />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <h4 className="truncate text-sm font-semibold text-game-primary dark:text-zinc-100">
                {character.name}
              </h4>
              {/* Pílula de papel, na forma do selo "NPC" que a ficha desenha.
                  As cores continuam vindo de getCharacterTypeStyle. */}
              {characterType !== 'jogador' && (
                <span className={cn(
                  'flex-shrink-0 rounded-full border px-1.5 text-[10px] font-bold uppercase leading-4',
                  typeStyle.badge
                )}>
                  {typeStyle.label}
                </span>
              )}
            </div>

            <p className="truncate text-xs text-game-muted">{character.className}</p>

            {/* Barra de HP: o relance que a coluna de números não dava */}
            <div className="mt-1 flex items-center gap-1.5">
              <Heart className={cn('h-3.5 w-3.5 flex-shrink-0', hp.texto)} />
              <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-game-quaternary dark:bg-zinc-900">
                <span
                  data-testid={`hp-barra-${character.id}`}
                  className={cn('block h-full rounded-full bg-gradient-to-r transition-[width] duration-500', hp.barra)}
                  style={{ width: `${hp.pct}%` }}
                />
              </span>
              <span className={cn('flex-shrink-0 text-[11px] font-bold tabular-nums', hp.texto)}>
                {hpCurrent}/{hpTotal}
              </span>
              {points > 0 && (
                <span className="flex flex-shrink-0 items-center gap-0.5 rounded-md bg-cyan-500/15 px-1.5 text-[11px] font-bold leading-5 tabular-nums text-cyan-600 dark:text-cyan-400">
                  <Zap className="h-3 w-3" />
                  {points}
                </span>
              )}
            </div>
          </div>
        </button>

        {/* Chat privado — só no personagem dos outros (as anotações pessoais
            têm área própria). 44px: era 32px, o mesmo problema que o X da
            ficha tinha. */}
        {onChatClick && !isOwnCharacter && (
          <button
            type="button"
            onClick={() => onChatClick(character)}
            title={`Chat privado com ${character.name}`}
            aria-label={`Chat privado com ${character.name}`}
            className="relative flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 transition-colors hover:bg-emerald-500/30 dark:text-emerald-400"
          >
            <MessageCircle className="h-[18px] w-[18px]" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-emerald-500 px-1 text-[10px] font-bold leading-none text-white shadow-lg">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
        )}
      </div>
    )
  }

  const tituloDoGrupo = (texto: string, quantos: number) => (
    <p className="px-1 pt-3 pb-1.5 text-[11px] font-bold uppercase tracking-wider text-game-muted">
      {texto} — {quantos}
    </p>
  )

  // Agrupar por presença só quando a presença é conhecida. Sem `isUserOnline`
  // (ainda carregando) vai lista única: não inventar presença que não se sabe.
  const online = isUserOnline ? sortedCharacters.filter(isRowOnline) : []
  const ausentes = isUserOnline ? sortedCharacters.filter(c => !isRowOnline(c)) : []

  return (
    <ScrollArea className="h-full">
      <div className="space-y-2 p-3">
        {!isUserOnline && sortedCharacters.map(cartao)}

        {isUserOnline && online.length > 0 && (
          <>
            {tituloDoGrupo('Na mesa agora', online.length)}
            <div className="space-y-2">{online.map(cartao)}</div>
          </>
        )}

        {isUserOnline && ausentes.length > 0 && (
          <>
            {tituloDoGrupo('Ausentes', ausentes.length)}
            <div className="space-y-2">{ausentes.map(cartao)}</div>
          </>
        )}

        {/* Estado vazio */}
        {characters.length === 0 && (
          <div className="px-4 py-8 text-center text-game-muted">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-game-tertiary dark:bg-zinc-800/50">
              <Sword className="h-6 w-6 text-game-muted" />
            </div>
            <h3 className="mb-1 text-sm font-medium text-game-secondary dark:text-zinc-400">Nenhum personagem</h3>
            <p className="mb-3 text-xs text-game-muted">
              Crie um personagem para começar
            </p>
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 border-game-border-primary text-xs text-game-muted hover:bg-game-hover hover:text-game-primary dark:border-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
            >
              <Plus className="h-3 w-3" />
              Criar
            </Button>
          </div>
        )}
      </div>
    </ScrollArea>
  )
}
